import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import Seo from "../components/Seo";
import {
  ArrowRight,
  CheckCircle,
  Cube,
  Package,
  Robot,
  ChartLineUp,
  Storefront,
  Database,
  Camera,
  Broadcast,
  Brain,
  Lightning,
  PresentationChart,
} from "@phosphor-icons/react";

const COPY = {
  cs: {
    seo: {
      title: "RetailStore Digitalisation · Reponio Store",
      desc:
        "Reponio Store: modulární platforma pro digitalizaci prodejních ploch. Smart Stand, Smart Kit a AI App pro plnohodnotnou digitalizaci kamenného retailu.",
    },
    eyebrow: "Pilíř 04 / RetailStore Digitalisation",
    brand: "Reponio Store",
    title: "Inovace, která mění retailová pravidla.",
    sub:
      "Reponio Store je modulární platforma společnosti REPONIO pro digitalizaci prodejních ploch. Global Marketing se podílel na vývoji tohoto řešení od jeho počátku, a jako partner pro Českou i Slovenskou republiku garantuje celou cestu od konzultace přes integraci až po zajištění provozu.",
    leadIn:
      "Přinášíme hlubokou znalost retailového prostředí, vztahy s řetězci a zkušenost s terénní realizací. Od návrhu prodejní plochy přes instalaci smart stojanu až po nastavení dashboardu a zaškolení týmu — vše zajišťujeme formou full-service.",
    metric: { k: "Full-service", v: "od konzultace po provoz · CZ + SK" },
    manifest: {
      eyebrow: "Manifest",
      h: "Offline retail zaslouží data. Přinášíme mu je.",
      body: [
        "Online svět žije daty v reálném čase. Kamenný retail zatím spoléhá na obchodní zástupce, ruční kontroly a zpětné reporty. Výsledkem jsou vysoké náklady, subjektivní informace a rozhodování naslepo.",
        "Reponio Store přináší do fyzického retailu stejnou úroveň dat, řízení a automatizace, jakou online svět zná už léta. A vy tak konečně víte, co se na vašich prodejních plochách skutečně děje.",
      ],
    },
    what: {
      eyebrow: "Co je Reponio Store?",
      h: "Modulární platforma pro digitalizaci prodejních ploch.",
      body: [
        "Reponio Store propojuje hardware, software a data do jediného systému pro řízení kamenného retailu. Digitalizuje vlastní stojany i regály, lednice nebo prodejní plochy třetích stran. A pokud nechcete hardware vůbec — nevadí. Platforma funguje i čistě softwarově.",
        "Výsledkem je aplikace, která v praxi nahrazuje roli tradičního obchodního zástupce a stává se vaším nástrojem digitálního merchandisingu.",
      ],
    },
    paths: {
      eyebrow: "Tři cesty ke stejnému cíli",
      h: "Všechny varianty sdílejí jednu silnou datovou a analytickou platformu.",
      items: [
        {
          tag: "Reponio Smart Stand",
          icon: "Cube",
          h: "Chytrý stojan s integrovanými technologiemi.",
          t: "Připravený k okamžitému nasazení. Plug & play řešení pro dodavatele, kteří chtějí výsledky od prvního dne.",
          mode: "HARDWARE · TURNKEY",
        },
        {
          tag: "Reponio Smart Kit",
          icon: "Package",
          h: "Modulární kit pro existující plochy.",
          t: "Přemění jakýkoliv regál, lednici nebo stojan na chytré prodejní místo. Technologie přichází k vám — ne naopak.",
          mode: "RETROFIT · ANY SHELF",
        },
        {
          tag: "Reponio App",
          icon: "Robot",
          h: "Software-only — bez hardwaru.",
          t: "Pro ty, kdo chtějí začít bez hardwaru. Terénní pracovník pořídí fotografii plochy a AI analýza udělá zbytek. Žádné formuláře. Žádné ruční reporty.",
          mode: "SOFTWARE · AI-FIRST",
        },
      ],
    },
    ai: {
      eyebrow: "AI, která pracuje za vás",
      h: "Zapomeňte na manuální reportování.",
      lead:
        "Reponio Store snižuje potřebu fyzických kontrol prodejních ploch o desítky procent a výrazně redukuje náklady na obchodní zástupce a merchandising.",
      stepsTitle: "Jak to funguje?",
      steps: [
        { n: "01", h: "Plocha v aplikaci", t: "Všechny prodejní plochy jsou vedeny přímo v aplikaci." },
        { n: "02", h: "Foto z terénu", t: "Terénní pracovník pořídí fotografii." },
        { n: "03", h: "AI analýza", t: "Aplikace spustí AI analýzu — bez vyplněných formulářů." },
        { n: "04", h: "Data v dashboardu", t: "Data se sama kategorizují, vyhodnocují a ukládají." },
      ],
      cards: [
        {
          icon: "Brain",
          h: "Prediktivní analytika",
          t: "Odhaluje trendy v prodejích a zásobách dřív, než se projeví.",
        },
        {
          icon: "PresentationChart",
          h: "Automatizované reporty",
          t: "Přesný obraz výkonu každého prodejního místa — bez ruční práce.",
        },
        {
          icon: "Lightning",
          h: "Identifikace příležitostí",
          t: "Upozornění na nevyužitý potenciál — od cross-sellingu po optimalizaci rozmístění produktů.",
        },
      ],
    },
    truth: {
      eyebrow: "Data, která říkají pravdu",
      h: "Konzistentní, srovnatelná a spolehlivá data pro skutečná obchodní rozhodnutí.",
      body:
        "Platforma sbírá data v reálném čase z celé řady zdrojů — pohybové radary měřící návštěvnost, váhové senzory sledující práci s produkty, GPS poloha prodejních ploch i mobilní fotografie přímo z terénu.",
      sources: [
        { h: "Pohybové radary", t: "Měření návštěvnosti prodejní plochy v reálném čase." },
        { h: "Váhové senzory", t: "Sledování práce s produkty a tempa odběru z police." },
        { h: "GPS lokalizace", t: "Přesná poloha prodejních ploch v terénu." },
        { h: "Mobilní fotografie", t: "Vstup z aplikace přímo z prodejny — kdykoli, odkudkoli." },
      ],
    },
    customer: {
      eyebrow: "Zákazník jako součást zážitku",
      h: "Reponio Store nekončí u dat.",
      body:
        "Platforma umožňuje zapojit zákazníky přímo v místě prodeje — prostřednictvím LED displejů, video marketingu, interaktivního audia a dynamických kampaní řízených reálnými daty. Obsah spravujete centrálně, na dálku, pro stovky ploch najednou.",
      bullets: [
        "LED displeje a video marketing",
        "Interaktivní audio prvky",
        "Dynamické kampaně řízené daty",
        "Centrální dálková správa stovek ploch",
      ],
    },
    benefits: {
      eyebrow: "Co digitalizace přináší vašemu byznysu?",
      h: "Méně nákladů, více kontroly, lepší rozhodnutí, rychlejší reakce.",
      items: [
        { kicker: "01", h: "Méně nákladů", t: "Méně lidí v terénu, méně chyb, méně zbytečných jízd." },
        { kicker: "02", h: "Více kontroly", t: "Centrální řízení stovek obchodních ploch z jediného místa." },
        { kicker: "03", h: "Lepší rozhodnutí", t: "Data místo dojmů, predikce místo zpětného pohledu." },
        { kicker: "04", h: "Rychlejší reakce", t: "Automatizace servisu, doplňování i kampaní bez čekání." },
      ],
    },
    audience: {
      eyebrow: "Pro koho je Reponio Store?",
      h: "Platforma funguje pro chytré i klasické prodejní plochy.",
      items: [
        {
          label: "Dodavatelé do retailu",
          t: "Kteří chtějí vědět, jak jejich produkty skutečně prodávají.",
          icon: "Package",
        },
        {
          label: "Retailové řetězce",
          t: "Hledající efektivnější správu svých prodejních ploch.",
          icon: "Storefront",
        },
        {
          label: "Výrobci retailového hardware",
          t: "Regálů, lednic a stojanů, kteří chtějí svým zákazníkům nabídnout více.",
          icon: "Cube",
        },
      ],
    },
    finalCta: {
      eyebrow: "Začněte digitalizovat",
      title: "Chcete vidět Reponio Store v provozu?",
      sub:
        "Navštivte nás v showroomu REPONIO, Business park Čestlice, Obchodní 126. Předvedeme vám smart stojany i software platformu. Domluvíme si konkrétní postup pro vaše výrobky — od pilotní plochy po celosíťové nasazení.",
      email: "info@globalmarketing.cz",
      button: "Kontaktujte nás",
      formCta: "→ Kontaktní formulář",
    },
  },
  en: {
    seo: {
      title: "RetailStore Digitalisation · Reponio Store",
      desc:
        "Reponio Store: a modular platform for digitalising the shop floor. Smart Stand, Smart Kit and AI App to bring real-time data into brick-and-mortar retail.",
    },
    eyebrow: "Pillar 04 / RetailStore Digitalisation",
    brand: "Reponio Store",
    title: "An innovation that rewrites retail rules.",
    sub:
      "Reponio Store is REPONIO's modular platform for digitalising the shop floor. Global Marketing has been part of this solution from day one and, as the partner for the Czech Republic and Slovakia, guarantees the whole journey — from consultation through integration to live operations.",
    leadIn:
      "We bring deep retail knowledge, relationships with chains and field-execution experience. From shop-floor design through smart-stand installation to dashboard setup and team training — we deliver everything as a full service.",
    metric: { k: "Full-service", v: "consultation to operations · CZ + SK" },
    manifest: {
      eyebrow: "Manifest",
      h: "Offline retail deserves data. We bring it.",
      body: [
        "The online world runs on real-time data. Brick-and-mortar retail still relies on sales reps, manual checks and lagging reports. The result: high cost, subjective information and decision-making in the dark.",
        "Reponio Store brings into physical retail the same level of data, control and automation that the online world has had for years. So you finally know what's actually happening on your shop floor.",
      ],
    },
    what: {
      eyebrow: "What is Reponio Store?",
      h: "A modular platform for digitalising the shop floor.",
      body: [
        "Reponio Store connects hardware, software and data into a single system for managing brick-and-mortar retail. It digitalises your own stands and shelves, fridges or third-party shop floors. And if you don't want hardware at all — that's fine. The platform works software-only.",
        "The result is an app that effectively replaces the role of a traditional sales rep and becomes your tool for digital merchandising.",
      ],
    },
    paths: {
      eyebrow: "Three paths to the same goal",
      h: "All variants share one strong data and analytics platform.",
      items: [
        {
          tag: "Reponio Smart Stand",
          icon: "Cube",
          h: "A smart stand with integrated technology.",
          t: "Ready for immediate deployment. A plug & play solution for suppliers who want results from day one.",
          mode: "HARDWARE · TURNKEY",
        },
        {
          tag: "Reponio Smart Kit",
          icon: "Package",
          h: "Modular kit for existing surfaces.",
          t: "Turns any shelf, fridge or stand into a smart point of sale. Technology comes to you — not the other way around.",
          mode: "RETROFIT · ANY SHELF",
        },
        {
          tag: "Reponio App",
          icon: "Robot",
          h: "Software-only — no hardware.",
          t: "For those who want to start without hardware. The field worker takes a photo of the surface and AI analysis does the rest. No forms. No manual reports.",
          mode: "SOFTWARE · AI-FIRST",
        },
      ],
    },
    ai: {
      eyebrow: "AI that works for you",
      h: "Forget manual reporting.",
      lead:
        "Reponio Store cuts the need for physical shop-floor checks by tens of percent and significantly reduces sales-rep and merchandising cost.",
      stepsTitle: "How does it work?",
      steps: [
        { n: "01", h: "Surfaces in the app", t: "All shop-floor surfaces are mapped directly in the application." },
        { n: "02", h: "Photo from the field", t: "The field worker takes a photo." },
        { n: "03", h: "AI analysis", t: "The app runs AI analysis — no forms to fill in." },
        { n: "04", h: "Data in the dashboard", t: "Data is auto-categorised, evaluated and stored." },
      ],
      cards: [
        {
          icon: "Brain",
          h: "Predictive analytics",
          t: "Surfaces sales and stock trends before they show up in the numbers.",
        },
        {
          icon: "PresentationChart",
          h: "Automated reports",
          t: "An accurate picture of every point of sale — without manual work.",
        },
        {
          icon: "Lightning",
          h: "Opportunity detection",
          t: "Flags untapped potential — from cross-selling to product-placement optimisation.",
        },
      ],
    },
    truth: {
      eyebrow: "Data that tells the truth",
      h: "Consistent, comparable and reliable data for real commercial decisions.",
      body:
        "The platform collects data in real time from a range of sources — motion radars measuring traffic, weight sensors tracking product handling, GPS positions of shop-floor surfaces and mobile photos straight from the field.",
      sources: [
        { h: "Motion radars", t: "Real-time measurement of shop-floor footfall." },
        { h: "Weight sensors", t: "Tracking product handling and pick rates from the shelf." },
        { h: "GPS positioning", t: "Exact location of shop-floor surfaces in the field." },
        { h: "Mobile photos", t: "App input straight from the store — anytime, anywhere." },
      ],
    },
    customer: {
      eyebrow: "Shopper as part of the experience",
      h: "Reponio Store doesn't stop at data.",
      body:
        "The platform lets you engage shoppers right at the point of sale — through LED displays, video marketing, interactive audio and dynamic campaigns driven by real data. You manage content centrally, remotely, across hundreds of surfaces at once.",
      bullets: [
        "LED displays and video marketing",
        "Interactive audio elements",
        "Dynamic data-driven campaigns",
        "Central remote control across hundreds of surfaces",
      ],
    },
    benefits: {
      eyebrow: "What digitalisation brings to your business",
      h: "Less cost, more control, better decisions, faster reaction.",
      items: [
        { kicker: "01", h: "Less cost", t: "Fewer people in the field, fewer errors, fewer wasted trips." },
        { kicker: "02", h: "More control", t: "Central management of hundreds of shop-floor surfaces from one place." },
        { kicker: "03", h: "Better decisions", t: "Data over impressions, prediction over hindsight." },
        { kicker: "04", h: "Faster reaction", t: "Service, replenishment and campaign automation — no waiting." },
      ],
    },
    audience: {
      eyebrow: "Who is Reponio Store for?",
      h: "The platform works for both smart and classic shop-floor surfaces.",
      items: [
        {
          label: "Retail suppliers",
          t: "Who want to know how their products actually sell.",
          icon: "Package",
        },
        {
          label: "Retail chains",
          t: "Looking for more efficient management of their shop-floor surfaces.",
          icon: "Storefront",
        },
        {
          label: "Retail hardware makers",
          t: "Of shelves, fridges and stands who want to offer their customers more.",
          icon: "Cube",
        },
      ],
    },
    finalCta: {
      eyebrow: "Start digitalising",
      title: "Want to see Reponio Store in action?",
      sub:
        "Visit us at the REPONIO showroom in Business Park Čestlice, Obchodní 126. We'll demo the smart stands and the software platform, and align a concrete plan for your products — from a pilot surface to a network-wide rollout.",
      email: "info@globalmarketing.cz",
      button: "Contact us",
      formCta: "→ Contact form",
    },
  },
  sk: {
    seo: {
      title: "RetailStore Digitalisation · Reponio Store",
      desc:
        "Reponio Store: modulárna platforma na digitalizáciu predajných plôch. Smart Stand, Smart Kit a AI App pre plnohodnotnú digitalizáciu kamenného retailu.",
    },
    eyebrow: "Pilier 04 / RetailStore Digitalisation",
    brand: "Reponio Store",
    title: "Inovácia, ktorá mení retailové pravidlá.",
    sub:
      "Reponio Store je modulárna platforma spoločnosti REPONIO na digitalizáciu predajných plôch. Global Marketing sa podieľal na vývoji tohto riešenia od jeho počiatku, a ako partner pre Českú aj Slovenskú republiku garantuje celú cestu od konzultácie cez integráciu až po zaistenie prevádzky.",
    leadIn:
      "Prinášame hlbokú znalosť retailového prostredia, vzťahy s reťazcami a skúsenosti s terénnou realizáciou. Od návrhu predajnej plochy cez inštaláciu smart stojanu až po nastavenie dashboardu a zaškolenie tímu — všetko zaisťujeme formou full-service.",
    metric: { k: "Full-service", v: "od konzultácie po prevádzku · CZ + SK" },
    manifest: {
      eyebrow: "Manifest",
      h: "Offline retail si zaslúži dáta. Prinášame mu ich.",
      body: [
        "Online svet žije dátami v reálnom čase. Kamenný retail zatiaľ spolieha na obchodných zástupcov, ručné kontroly a oneskorené reporty. Výsledkom sú vysoké náklady, subjektívne informácie a rozhodovanie naslepo.",
        "Reponio Store prináša do fyzického retailu rovnakú úroveň dát, riadenia a automatizácie, akú online svet pozná už roky. A vy tak konečne viete, čo sa na vašich predajných plochách skutočne deje.",
      ],
    },
    what: {
      eyebrow: "Čo je Reponio Store?",
      h: "Modulárna platforma na digitalizáciu predajných plôch.",
      body: [
        "Reponio Store prepája hardvér, softvér a dáta do jediného systému na riadenie kamenného retailu. Digitalizuje vlastné stojany aj regály, chladničky alebo predajné plochy tretích strán. A pokiaľ nechcete hardvér vôbec — nevadí. Platforma funguje aj čisto softvérovo.",
        "Výsledkom je aplikácia, ktorá v praxi nahrádza úlohu tradičného obchodného zástupcu a stáva sa vaším nástrojom digitálneho merchandisingu.",
      ],
    },
    paths: {
      eyebrow: "Tri cesty k rovnakému cieľu",
      h: "Všetky varianty zdieľajú jednu silnú dátovú a analytickú platformu.",
      items: [
        {
          tag: "Reponio Smart Stand",
          icon: "Cube",
          h: "Chytrý stojan s integrovanými technológiami.",
          t: "Pripravený na okamžité nasadenie. Plug & play riešenie pre dodávateľov, ktorí chcú výsledky od prvého dňa.",
          mode: "HARDWARE · TURNKEY",
        },
        {
          tag: "Reponio Smart Kit",
          icon: "Package",
          h: "Modulárny kit pre existujúce plochy.",
          t: "Premení akýkoľvek regál, chladničku alebo stojan na chytré predajné miesto. Technológia prichádza k vám — nie naopak.",
          mode: "RETROFIT · ANY SHELF",
        },
        {
          tag: "Reponio App",
          icon: "Robot",
          h: "Software-only — bez hardvéru.",
          t: "Pre tých, ktorí chcú začať bez hardvéru. Terénny pracovník vyhotoví fotografiu plochy a AI analýza urobí zvyšok. Žiadne formuláre. Žiadne ručné reporty.",
          mode: "SOFTWARE · AI-FIRST",
        },
      ],
    },
    ai: {
      eyebrow: "AI, ktorá pracuje za vás",
      h: "Zabudnite na manuálne reportovanie.",
      lead:
        "Reponio Store znižuje potrebu fyzických kontrol predajných plôch o desiatky percent a výrazne redukuje náklady na obchodných zástupcov a merchandising.",
      stepsTitle: "Ako to funguje?",
      steps: [
        { n: "01", h: "Plocha v aplikácii", t: "Všetky predajné plochy sú vedené priamo v aplikácii." },
        { n: "02", h: "Foto z terénu", t: "Terénny pracovník vyhotoví fotografiu." },
        { n: "03", h: "AI analýza", t: "Aplikácia spustí AI analýzu — bez vyplnených formulárov." },
        { n: "04", h: "Dáta v dashboarde", t: "Dáta sa samy kategorizujú, vyhodnocujú a ukladajú." },
      ],
      cards: [
        {
          icon: "Brain",
          h: "Prediktívna analytika",
          t: "Odhaľuje trendy v predaji a zásobách skôr, než sa prejavia.",
        },
        {
          icon: "PresentationChart",
          h: "Automatizované reporty",
          t: "Presný obraz výkonu každého predajného miesta — bez ručnej práce.",
        },
        {
          icon: "Lightning",
          h: "Identifikácia príležitostí",
          t: "Upozornenia na nevyužitý potenciál — od cross-sellingu po optimalizáciu rozmiestnenia produktov.",
        },
      ],
    },
    truth: {
      eyebrow: "Dáta, ktoré hovoria pravdu",
      h: "Konzistentné, porovnateľné a spoľahlivé dáta pre skutočné obchodné rozhodnutia.",
      body:
        "Platforma zbiera dáta v reálnom čase z celej rady zdrojov — pohybové radary merajúce návštevnosť, váhové senzory sledujúce prácu s produktmi, GPS poloha predajných plôch aj mobilné fotografie priamo z terénu.",
      sources: [
        { h: "Pohybové radary", t: "Meranie návštevnosti predajnej plochy v reálnom čase." },
        { h: "Váhové senzory", t: "Sledovanie práce s produktmi a tempa odberu z police." },
        { h: "GPS lokalizácia", t: "Presná poloha predajných plôch v teréne." },
        { h: "Mobilné fotografie", t: "Vstup z aplikácie priamo z predajne — kedykoľvek, odkiaľkoľvek." },
      ],
    },
    customer: {
      eyebrow: "Zákazník ako súčasť zážitku",
      h: "Reponio Store nekončí pri dátach.",
      body:
        "Platforma umožňuje zapojiť zákazníkov priamo na mieste predaja — prostredníctvom LED displejov, video marketingu, interaktívneho audia a dynamických kampaní riadených reálnymi dátami. Obsah spravujete centrálne, na diaľku, pre stovky plôch naraz.",
      bullets: [
        "LED displeje a video marketing",
        "Interaktívne audio prvky",
        "Dynamické kampane riadené dátami",
        "Centrálna diaľková správa stoviek plôch",
      ],
    },
    benefits: {
      eyebrow: "Čo digitalizácia prináša vášmu biznisu?",
      h: "Menej nákladov, viac kontroly, lepšie rozhodnutia, rýchlejšia reakcia.",
      items: [
        { kicker: "01", h: "Menej nákladov", t: "Menej ľudí v teréne, menej chýb, menej zbytočných jázd." },
        { kicker: "02", h: "Viac kontroly", t: "Centrálne riadenie stoviek obchodných plôch z jediného miesta." },
        { kicker: "03", h: "Lepšie rozhodnutia", t: "Dáta namiesto dojmov, predikcia namiesto spätného pohľadu." },
        { kicker: "04", h: "Rýchlejšia reakcia", t: "Automatizácia servisu, doplňovania aj kampaní bez čakania." },
      ],
    },
    audience: {
      eyebrow: "Pre koho je Reponio Store?",
      h: "Platforma funguje pre chytré aj klasické predajné plochy.",
      items: [
        {
          label: "Dodávatelia do retailu",
          t: "Ktorí chcú vedieť, ako ich produkty skutočne predávajú.",
          icon: "Package",
        },
        {
          label: "Retailové reťazce",
          t: "Hľadajúce efektívnejšiu správu svojich predajných plôch.",
          icon: "Storefront",
        },
        {
          label: "Výrobcovia retailového hardvéru",
          t: "Regálov, chladničiek a stojanov, ktorí chcú svojim zákazníkom ponúknuť viac.",
          icon: "Cube",
        },
      ],
    },
    finalCta: {
      eyebrow: "Začnite digitalizovať",
      title: "Chcete vidieť Reponio Store v prevádzke?",
      sub:
        "Navštívte nás v showroome REPONIO, Business park Čestlice, Obchodní 126. Predvedieme vám smart stojany aj softvérovú platformu. Dohodneme si konkrétny postup pre vaše výrobky — od pilotnej plochy po celosieťové nasadenie.",
      email: "info@globalmarketing.cz",
      button: "Kontaktujte nás",
      formCta: "→ Kontaktný formulár",
    },
  },
};

const ICONS = {
  Cube,
  Package,
  Robot,
  ChartLineUp,
  Storefront,
  Database,
  Camera,
  Broadcast,
  Brain,
  Lightning,
  PresentationChart,
};

const Eyebrow = ({ children, accent, light }) => (
  <span className={`gm-eyebrow ${accent ? "text-[#0A4DFF]" : light ? "text-white/60" : ""}`}>{children}</span>
);

const SlideFrame = ({ src, alt, theme = "light", testid }) => (
  <figure
    className={`border ${
      theme === "dark" ? "border-white/15 bg-white/5" : "border-[#e5e5e5] bg-white"
    } overflow-hidden`}
    data-testid={testid}
  >
    <img src={src} alt={alt} loading="lazy" className="block w-full h-auto" />
  </figure>
);

const Hero = ({ c }) => (
  <section className="relative bg-white border-b border-[#e5e5e5]" data-testid="rd-hero">
    <div className="absolute inset-0 gm-grid-bg pointer-events-none" />
    <div className="max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-14 md:pb-20 relative">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-9">
          <Eyebrow accent>{c.eyebrow}</Eyebrow>
          <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-[#0a0a0a] text-white">
            <span className="w-1.5 h-1.5 bg-[#0A4DFF]" />
            <span className="font-mono text-[11px] tracking-wider uppercase">{c.brand}</span>
          </div>
          <h1 className="font-display text-5xl md:text-7xl tracking-tighter font-medium leading-[0.95] mt-6 max-w-4xl">
            {c.title}
          </h1>
          <p className="text-lg md:text-xl text-[#525252] mt-8 max-w-3xl leading-relaxed">{c.sub}</p>
          <p className="text-base text-[#0a0a0a] mt-6 max-w-3xl leading-relaxed border-l-2 border-[#0A4DFF] pl-5">
            {c.leadIn}
          </p>
        </div>
        <div className="lg:col-span-3 flex lg:items-end">
          <div className="bg-[#0A4DFF] text-white p-6 w-full">
            <div className="font-mono text-[10px] tracking-widest text-white/70">// MODE</div>
            <div className="font-display text-3xl md:text-4xl tracking-tight mt-3 leading-tight">{c.metric.k}</div>
            <div className="text-sm text-white/85 mt-3 leading-relaxed">{c.metric.v}</div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Manifest = ({ c }) => (
  <section className="py-14 md:py-20 bg-[#0a0a0a] text-white" data-testid="rd-manifest">
    <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
      <div className="md:col-span-5">
        <h2 className="font-display text-3xl md:text-5xl tracking-tight leading-[1.05] font-medium">{c.h}</h2>
      </div>
      <div className="md:col-span-7 space-y-5">
        {c.body.map((p, i) => (
          <p key={i} className="text-white/80 leading-relaxed">{p}</p>
        ))}
      </div>
    </div>
  </section>
);

const What = ({ c }) => (
  <section className="py-14 md:py-20 bg-white border-y border-[#e5e5e5]" data-testid="rd-what">
    <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
      <div className="md:col-span-6">
        <Eyebrow accent>{c.eyebrow}</Eyebrow>
        <h2 className="font-display text-3xl md:text-5xl tracking-tight mt-4 leading-[1.05] font-medium">{c.h}</h2>
        <div className="space-y-5 mt-8">
          {c.body.map((p, i) => (
            <p key={i} className="text-[#0a0a0a] leading-relaxed">{p}</p>
          ))}
        </div>
      </div>
      <div className="md:col-span-6">
        <SlideFrame
          src="/reponio/dashboard.jpg?v=3"
          alt="Reponio Store dashboard — analytika prodejních ploch a propojený kamenný retail"
          testid="rd-what-slide"
        />
      </div>
    </div>
  </section>
);

const Paths = ({ c }) => (
  <section className="py-14 md:py-20 bg-[#fafafa]" data-testid="rd-paths">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <Eyebrow>{c.eyebrow}</Eyebrow>
      <h2 className="font-display text-3xl md:text-5xl tracking-tight mt-4 leading-[1.05] font-medium max-w-4xl">
        {c.h}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-10">
        {c.items.map((it, i) => {
          const Icon = ICONS[it.icon];
          const dark = i === 1;
          return (
            <div
              key={i}
              className={`p-7 md:p-9 border ${
                dark ? "bg-[#0a0a0a] text-white border-[#0a0a0a]" : "bg-white border-[#e5e5e5]"
              }`}
              data-testid={`rd-path-${i}`}
            >
              <div className="flex items-start justify-between gap-4">
                <span
                  className={`font-mono text-[10px] tracking-widest ${
                    dark ? "text-[#5B82FF]" : "text-[#0A4DFF]"
                  }`}
                >
                  {it.mode}
                </span>
                {Icon ? (
                  <Icon
                    size={28}
                    weight="thin"
                    className={dark ? "text-white" : "text-[#0a0a0a]"}
                  />
                ) : null}
              </div>
              <span
                className={`block font-mono uppercase tracking-[0.22em] text-[12px] mt-6 ${
                  dark ? "text-white/70" : "text-[#525252]"
                }`}
              >
                {it.tag}
              </span>
              <h3 className="font-display text-2xl md:text-3xl mt-3 leading-tight tracking-tight">{it.h}</h3>
              <p
                className={`text-sm md:text-base mt-4 leading-relaxed ${
                  dark ? "text-white/75" : "text-[#525252]"
                }`}
              >
                {it.t}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

const Ai = ({ c }) => (
  <section className="py-14 md:py-20 bg-white border-y border-[#e5e5e5]" data-testid="rd-ai">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
        <div className="md:col-span-7">
          <Eyebrow accent>{c.eyebrow}</Eyebrow>
          <h2 className="font-display text-3xl md:text-5xl tracking-tight mt-4 leading-[1.05] font-medium">
            {c.h}
          </h2>
        </div>
        <p className="md:col-span-5 text-[#525252] leading-relaxed">{c.lead}</p>
      </div>

      <div className="mt-12">
        <span className="gm-eyebrow">// {c.stepsTitle}</span>
        <div className="grid grid-cols-1 md:grid-cols-4 mt-5 border border-[#e5e5e5]">
          {c.steps.map((p, i) => (
            <div
              key={i}
              className="p-6 md:p-7 border-b md:border-b-0 last:border-b-0 border-[#e5e5e5] md:border-r last:md:border-r-0"
            >
              <div className="font-display text-5xl tracking-tighter text-[#0A4DFF]/40">{p.n}</div>
              <h3 className="font-display text-lg md:text-xl mt-4 leading-tight">{p.h}</h3>
              <p className="text-sm text-[#525252] mt-3 leading-relaxed">{p.t}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-12">
        {c.cards.map((card, i) => {
          const Icon = ICONS[card.icon];
          return (
            <div key={i} className="border-t-2 border-[#0a0a0a] pt-5">
              <div className="flex items-center gap-3">
                {Icon ? <Icon size={22} weight="thin" className="text-[#0A4DFF]" /> : null}
                <span className="font-mono text-[10px] tracking-widest text-[#525252]">{`AI.0${i + 1}`}</span>
              </div>
              <h4 className="font-display text-xl md:text-2xl mt-3 tracking-tight leading-tight">{card.h}</h4>
              <p className="text-sm text-[#525252] mt-3 leading-relaxed">{card.t}</p>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

const Truth = ({ c }) => (
  <section className="py-14 md:py-20 bg-[#fafafa]" data-testid="rd-truth">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
        <div className="md:col-span-7">
          <Eyebrow>{c.eyebrow}</Eyebrow>
          <h2 className="font-display text-3xl md:text-5xl tracking-tight mt-4 leading-[1.05] font-medium">
            {c.h}
          </h2>
        </div>
        <p className="md:col-span-5 text-[#525252] leading-relaxed">{c.body}</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-5 mt-10 items-start">
        <div className="md:col-span-5">
          <SlideFrame
            src="/reponio/fridge.jpg?v=3"
            alt="Reponio Store — sběr dat v reálném čase z chytré chladničky"
            caption="Real-time data · fridge"
            testid="rd-truth-slide"
          />
        </div>
        <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
          {c.sources.map((s, i) => (
            <div key={i} className="bg-white border border-[#e5e5e5] p-6">
              <span className="font-mono text-[10px] tracking-widest text-[#525252]">{`SRC.0${i + 1}`}</span>
              <h4 className="font-display text-xl md:text-2xl mt-3 tracking-tight leading-tight">{s.h}</h4>
              <p className="text-sm text-[#525252] mt-3 leading-relaxed">{s.t}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const Customer = ({ c }) => (
  <section className="py-14 md:py-20 bg-[#0A4DFF] text-white" data-testid="rd-customer">
    <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
      <div className="md:col-span-5">
        <span className="font-mono uppercase tracking-[0.22em] text-[12px] text-white/80">{c.eyebrow}</span>
        <h2 className="font-display text-3xl md:text-5xl tracking-tight mt-4 leading-[1.05] font-medium">{c.h}</h2>
        <div className="mt-8">
          <SlideFrame
            src="/reponio/multimedia.jpg?v=3"
            alt="Reponio Store — interaktivní LED display Bohemia Sekt"
            caption="Interactive multimedia"
            theme="dark"
            testid="rd-customer-slide"
          />
        </div>
      </div>
      <div className="md:col-span-7">
        <p className="text-white/95 leading-relaxed text-base md:text-lg">{c.body}</p>
        <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6">
          {c.bullets.map((b, i) => (
            <li
              key={i}
              className="py-3 flex items-start gap-3 text-sm leading-relaxed border-b border-white/20"
            >
              <CheckCircle size={16} weight="thin" className="mt-0.5 shrink-0" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
        <div className="mt-6">
          <SlideFrame
            src="/reponio/app.jpg?v=3"
            alt="Reponio Store — digitalizovaná správa prodejních ploch v praxi"
            caption="Digitalised management in practice"
            theme="dark"
            testid="rd-customer-slide-2"
          />
        </div>
      </div>
    </div>
  </section>
);

const Benefits = ({ c }) => (
  <section className="py-14 md:py-20 bg-white border-y border-[#e5e5e5]" data-testid="rd-benefits">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <Eyebrow accent>{c.eyebrow}</Eyebrow>
      <h2 className="font-display text-3xl md:text-5xl tracking-tight mt-4 leading-[1.05] font-medium max-w-4xl">
        {c.h}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
        {c.items.map((it, i) => (
          <div key={i} className="border-t-2 border-[#0a0a0a] pt-5">
            <span className="font-mono text-[10px] tracking-widest text-[#525252]">{`B.${it.kicker}`}</span>
            <h3 className="font-display text-xl md:text-2xl mt-3 tracking-tight leading-tight">{it.h}</h3>
            <p className="text-sm text-[#525252] mt-3 leading-relaxed">{it.t}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Audience = ({ c }) => (
  <section className="py-14 md:py-20 bg-[#fafafa]" data-testid="rd-audience">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
        <div className="md:col-span-7">
          <Eyebrow>{c.eyebrow}</Eyebrow>
          <h2 className="font-display text-3xl md:text-5xl tracking-tight mt-4 leading-[1.05] font-medium max-w-4xl">
            {c.h}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mt-10">
            {c.items.map((it, i) => {
              const Icon = ICONS[it.icon];
              return (
                <div key={i} className="bg-white border border-[#e5e5e5] p-7">
                  <div className="flex items-center gap-3">
                    {Icon ? <Icon size={22} weight="thin" className="text-[#0A4DFF]" /> : null}
                    <span className="gm-eyebrow text-[#0A4DFF]">{it.label}</span>
                  </div>
                  <p className="text-[#0a0a0a] mt-4 leading-relaxed">{it.t}</p>
                </div>
              );
            })}
          </div>
        </div>
        <div className="md:col-span-5">
          <SlideFrame
            src="/reponio/store.jpg?v=3"
            alt="Reponio Store — prodejna, kde technologie neomezuje, ale osvobozuje"
            testid="rd-audience-slide"
          />
        </div>
      </div>
    </div>
  </section>
);

const FinalCta = ({ c }) => (
  <section className="py-14 md:py-20 bg-[#0a0a0a] text-white" data-testid="rd-final-cta">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <span className="font-mono uppercase tracking-[0.22em] text-[12px] text-[#5B82FF]">{c.eyebrow}</span>
      <h2 className="font-display text-4xl md:text-6xl tracking-tighter mt-5 leading-[0.95] font-medium max-w-4xl">
        {c.title}
      </h2>
      <p className="text-white/80 mt-5 leading-relaxed max-w-2xl text-base md:text-lg">{c.sub}</p>
      <div className="mt-8 flex flex-wrap items-center gap-4">
        <Link to="/kontakt" className="gm-btn gm-btn-primary" data-testid="rd-cta-kontakt">
          {c.button} <ArrowRight size={16} />
        </Link>
        <a
          href={`mailto:${c.email}`}
          className="text-sm font-mono tracking-widest uppercase text-white border-b border-white/30 pb-1 hover:border-white"
          data-testid="rd-cta-email"
        >
          {c.email}
        </a>
      </div>
    </div>
  </section>
);

export default function RetailDigitalisation() {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.cs;
  return (
    <div data-testid="retail-digitalisation-page">
      <Seo
        title={{ cs: COPY.cs.seo.title, en: COPY.en.seo.title }}
        description={{ cs: COPY.cs.seo.desc, en: COPY.en.seo.desc }}
        path="/retail-digitalisation"
      />
      <Hero c={c} />
      <Manifest c={c.manifest} />
      <What c={c.what} />
      <Paths c={c.paths} />
      <Ai c={c.ai} />
      <Truth c={c.truth} />
      <Customer c={c.customer} />
      <Benefits c={c.benefits} />
      <Audience c={c.audience} />
      <FinalCta c={c.finalCta} />
    </div>
  );
}
